import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import { FC, useEffect } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as yup from 'yup';

import { BillingCollectionMethod, useCreateSubscriptionMutation } from '@treams/treams-network';
import { Button, GraphQLErrorMessage, Heading3, Input, Label, Select } from '@treams/treams-ui';

const addressShape = {
	line1: yup.string(),
	line2: yup.string(),
	postalCode: yup.string(),
	city: yup.string(),
	country: yup.string(),
};

const addressSchema = yup.object(addressShape);

const validationSchema = yup.object({
	email: yup.string().email('errors.validation.email').required('errors.validation.required'),
	startDate: yup.date().min(moment().subtract(1, 'days'), 'errors.validation.minimalToday'),
	collectionMethod: yup.string().required('errors.validation.required'),
	address: addressSchema.when('collectionMethod', {
		is: (val: BillingCollectionMethod) => val === BillingCollectionMethod.SendInvoice,
		then: (schema: typeof addressSchema) =>
			schema.shape({
				line1: yup.string().required('errors.validation.required'),
				postalCode: yup.string().required('errors.validation.required'),
				city: yup.string().required('errors.validation.required'),
				country: yup.string().required('errors.validation.required'),
			}),
	}),
});

const FormContainer = styled(Flex)`
	gap: 8px;
`;

type Props = {
	workspaceID: string;
};

export const AddStripeBillingConfigurationForm: FC<Props> = ({ workspaceID }) => {
	const [createSubscription, { error, loading }] = useCreateSubscriptionMutation();

	const form = useFormik({
		validationSchema,
		initialValues: {
			email: '',
			collectionMethod: BillingCollectionMethod.SendInvoice,
			startDate: new Date().toISOString().split('T')[0],
			address: {
				line1: '',
				line2: '',
				postalCode: '',
				city: '',
				country: '',
			},
		},
		onSubmit: async ({ email, startDate, collectionMethod, address }) => {
			const result = await createSubscription({
				variables: {
					input: {
						workspaceID,
						collectionMethod,
						email,
						startDate,
						...(form.values.collectionMethod === BillingCollectionMethod.SendInvoice
							? {
									address: {
										line1: address.line1,
										line2: address.line2,
										postalCode: address.postalCode,
										city: address.city,
										country: address.country,
									},
								}
							: {}),
					},
				},
			});

			if (result.data?.createSubscription.redirect) {
				window.open(result.data.createSubscription.redirect, '_blank');
			}

			return;
		},
	});

	useEffect(() => {
		form.resetForm();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workspaceID]);

	return (
		<FormikProvider value={form}>
			<FormContainer justifyContent="center" flexDirection="column">
				<Box>{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}</Box>
				<Box>
					<Input.Formik
						small
						label="Facturatie email"
						type="email"
						name="email"
						placeholder="facturatie@treams.nl"
						error={form.errors.email}
					/>
				</Box>
				<Box>
					<Input.Formik
						small
						type="date"
						name="startDate"
						error={form.errors.startDate}
						label="Startdatum"
					/>
				</Box>
				<Box>
					<Label>Betalingsmethode</Label>
					<Select
						search={false}
						small
						options={[
							{ title: 'Alleen factuur per email', value: BillingCollectionMethod.SendInvoice },
							{
								title: 'Automatisch afschrijven',
								value: BillingCollectionMethod.ChargeAutomatically,
							},
						]}
						onChange={(value) => {
							form.setFieldValue('collectionMethod', value);
						}}
						selectedValue={form.values.collectionMethod}
					/>
				</Box>
				{form.values.collectionMethod === BillingCollectionMethod.SendInvoice && (
					<>
						<Heading3>Adresgegevens</Heading3>
						<Box>
							<Input.Formik
								small
								label="Adres regel 1"
								type="text"
								name="address.line1"
								placeholder="Niemandstraat 1"
								error={form.errors.address?.line1}
							/>
						</Box>
						<Box>
							<Input.Formik
								small
								label="Adres regel 2"
								type="text"
								name="address.line2"
								placeholder="Appartement B"
								error={form.errors.address?.line2}
							/>
						</Box>
						<Box>
							<Input.Formik
								small
								label="Postcode"
								type="text"
								name="address.postalCode"
								placeholder="1234AA"
								error={form.errors.address?.postalCode}
							/>
						</Box>
						<Box>
							<Input.Formik
								small
								label="Stad"
								type="text"
								name="address.city"
								placeholder="Amsterdam"
								error={form.errors.address?.city}
							/>
						</Box>
						<Box>
							<Input.Formik
								small
								label="Land"
								type="text"
								name="address.country"
								placeholder="NL, BE, DE of FR"
								error={form.errors.address?.country}
							/>
						</Box>
					</>
				)}
				<Button.Formik
					type="primary"
					title={`Facturatie starten op ${form.values.startDate}`}
					small
					mt={2}
					loading={loading}
					disabled={loading || !form.isValid}
					disableSuccess
					onClick={() => form.handleSubmit()}
				/>
			</FormContainer>
		</FormikProvider>
	);
};
